import React, { Fragment, useEffect } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';

import { Faq } from './Faq';
import MoreHelpFaq from './MoreHelpFaq';

import Card from 'components/Shared/Card/Card';
import SegmentIO from 'reporting/SegmentIO';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { modalActions } from 'store/modal/slice';
import { RootState } from 'store/store';
import { walletSelectors } from 'store/wallet/selectors';
import { ShowModalFunc } from 'types/Modal';

type FaqType = {
  question: string;
  answer: string;
  questionNumber: number;
};

interface FaqContainerProps extends WrappedComponentProps {
  showModal: ShowModalFunc;
  sale: any;
  wallet: any;
  companyInfo: any;
  payment: any;
}

export const generateFaqData = (
  isAutopayEligible: boolean = false,
  isCreditCardEnabled: boolean = false,
  merchantName: string = 'your merchant'
): FaqType[] => {
  const faqData: FaqType[] = [];

  // First question - Autopay
  if (isAutopayEligible) {
    faqData.push({
      question: 'How can I set up Autopay?',
      answer:
        "Autopay allows you to automatically pay invoices on their due date. To set up Autopay for this invoice, Sign in or create an account. Then you'd be able to set up 'Autopay'. You'll need to select a default payment method and pay.",
      questionNumber: 1,
    });
  } else {
    faqData.push({
      question: 'How can I set up Autopay?',
      answer: `Autopay was not enabled for this invoice by your merchant, you could send a message to them asking them to enable it.`,
      questionNumber: 2,
    });
  }

  // Second question - Payment methods
  if (!isCreditCardEnabled) {
    faqData.push({
      question: 'How can I pay with CC?',
      answer: `Credit Card is a supported payment method on our platform, but it hasn't been enabled by ${merchantName} for this payment yet.`,
      questionNumber: 3,
    });
  } else {
    faqData.push({
      question: 'How can I pay with Apple Pay?',
      answer:
        'Apple Pay is enabled for this payment but you need to use an Apple device and Safari browser to complete the payment with Apple Pay.',
      questionNumber: 4,
    });
  }

  return faqData;
};

const FaqContainer: React.FC<FaqContainerProps> = ({
  showModal,
  sale,
  wallet,
  companyInfo,
  payment,
}: FaqContainerProps) => {
  const isAutopayEligible = sale?.scheduleInfo?.autoPayEligible ?? false;
  const enabledPaymentMethods = walletSelectors.enabledPaymentMethods(wallet);
  const isCreditCardEnabled =
    enabledPaymentMethods?.includes('cc') || enabledPaymentMethods?.includes('dc');
  const merchantName = companyInfoSelectors.nameSelector(companyInfo) || 'your merchant';
  const domainId = sale?.entity?.globalId?.localId || sale?.id;
  const faqData = generateFaqData(isAutopayEligible, isCreditCardEnabled, merchantName);

  useEffect(() => {
    SegmentIO.aiAssistViewed({
      ai_assist_test_name: 'treatment',
      domain_id: domainId,
      allowed_payment_methods: enabledPaymentMethods,
      feature_eligibility: {
        isAutopayEligible: isAutopayEligible ?? false,
        isAutoPayOn: payment?.isAutoPayOn ?? false,
      },
    });
  }, []);

  const handleQuestionOpen = (questionNumber: number) => {
    SegmentIO.aiAssistQuestionOpened({
      domain_id: domainId,
      questionNumber,
    });
  };

  return (
    <Fragment>
      <Card>
        {faqData?.map((faq: FaqType, index: number) => (
          <Faq
            key={index}
            question={faq.question}
            answer={faq.answer}
            onQuestionOpen={() => handleQuestionOpen(faq.questionNumber)}
          />
        ))}
        <MoreHelpFaq showModal={showModal} />
      </Card>
    </Fragment>
  );
};

const withIntl = injectIntl(FaqContainer);

function mapStateToProps(store: RootState) {
  const { sale, wallet, companyInfo, payment } = store;
  return {
    sale,
    wallet,
    companyInfo,
    payment,
  };
}

export default connect(mapStateToProps, {
  showModal: modalActions.show,
})(withIntl);
